import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Check, RotateCw, Upload, X } from "lucide-react";
import LogoBars from "@/components/brand/LogoBars";
import UploadProgressMark from "@/components/brand/UploadProgressMark";
import { useUnloadWarning } from "@/hooks/useUnloadWarning";

export type UploadStatus =
  | "queued"
  | "uploading"
  | "uploaded"
  | "failed";

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  contentType: string;
  status: UploadStatus;
  progress: number; // 0..100
  path?: string;
  error?: string;
}

export const DEFAULT_ACCEPT =
  ".pdf,.dwg,.dxf,.zip,.xls,.xlsx,.csv,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic,.ppt,.pptx";

const ALLOWED_EXT = new Set([
  "pdf", "dwg", "dxf", "zip", "xls", "xlsx", "csv",
  "doc", "docx", "jpg", "jpeg", "png", "webp", "heic",
  "ppt", "pptx",
]);

interface Props {
  source: "lighting_design" | "value_analysis" | "labs" | "talent";
  files: UploadedFile[];
  onChange: (files: UploadedFile[]) => void;
  maxFiles?: number;
  maxFileBytes?: number;
  maxTotalBytes?: number;
  disabled?: boolean;
  variant?: "dark" | "light";
  label?: string;
  helperText?: string;
  singleFile?: boolean;
  /** Optional non-PII analytics callbacks. Payloads MUST be counts / classes only — never filenames or storage keys. */
  onUploadStart?: (info: { count: number }) => void;
  onUploadComplete?: (info: { count: number; durationMs: number }) => void;
  onUploadFailed?: (info: { errorClass: string }) => void;
  onUploadRemoved?: (info: { remaining: number }) => void;
}

const DEFAULT_MAX_FILES = 10;
const DEFAULT_MAX_FILE_BYTES = 300 * 1024 * 1024; // 300 MB per file
const DEFAULT_MAX_TOTAL_BYTES = 3 * 1024 * 1024 * 1024; // 3 GB per submission
const OVERSIZE_HELP =
  "This file exceeds the 300 MB upload limit. If your project files are larger than 300 MB, please contact info@clumen.com and we'll arrange a secure method for transferring larger project packages.";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function extOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i + 1).toLowerCase() : "";
}

function randomId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

async function requestSignedUrl(
  source: string,
  filename: string,
  size: number,
): Promise<{ path: string; signedUrl: string; token: string }> {
  const { data, error } = await supabase.functions.invoke("create-upload-url", {
    body: { source, filename, size },
  });
  if (error) {
    // Try to surface the server's friendly error message instead of the
    // generic "non-2xx status code" wrapper from supabase-js.
    let detail: string | undefined;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.text === "function") {
        const raw = await ctx.text();
        try {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.error === "string") detail = parsed.error;
        } catch {
          if (raw) detail = raw;
        }
      }
    } catch {
      /* noop */
    }
    throw new Error(detail || error.message || "Could not create upload URL");
  }
  if (!data?.signedUrl || !data?.path) throw new Error("Missing signed URL");
  return data;
}

function uploadWithProgress(
  signedUrl: string,
  file: File,
  onProgress: (pct: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl, true);
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream",
    );
    xhr.setRequestHeader("x-upsert", "false");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

const FileUploader = ({
  source,
  files,
  onChange,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileBytes = DEFAULT_MAX_FILE_BYTES,
  maxTotalBytes = DEFAULT_MAX_TOTAL_BYTES,
  disabled,
  variant = "dark",
  label,
  helperText,
  singleFile = false,
  onUploadStart,
  onUploadComplete,
  onUploadFailed,
  onUploadRemoved,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const filesRef = useRef(files);
  const [dragOver, setDragOver] = useState(false);
  // Defensive guard for any stale parent prop still passing the former 250 MB cap.
  const effectiveMaxFileBytes = Math.max(maxFileBytes, DEFAULT_MAX_FILE_BYTES);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const patch = useCallback(
    (id: string, next: Partial<UploadedFile>) => {
      const current = filesRef.current;
      const updated = current.map((f) => (f.id === id ? { ...f, ...next } : f));
      filesRef.current = updated;
      onChange(updated);
    },
    [onChange],
  );

  const startUpload = useCallback(
    async (item: UploadedFile) => {
      const controller = new AbortController();
      controllersRef.current.set(item.id, controller);
      patch(item.id, { status: "uploading", progress: 0, error: undefined });
      const startedAt = Date.now();
      try {
        const { path, signedUrl } = await requestSignedUrl(
          source,
          item.name,
          item.size,
        );
        await uploadWithProgress(
          signedUrl,
          item.file,
          (pct) => patch(item.id, { progress: pct }),
          controller.signal,
        );
        patch(item.id, { status: "uploaded", progress: 100, path });
        onUploadComplete?.({ count: 1, durationMs: Date.now() - startedAt });
      } catch (err) {
        const msg =
          err instanceof DOMException && err.name === "AbortError"
            ? "Canceled"
            : err instanceof Error
              ? err.message
              : "Upload failed";
        if (msg !== "Canceled") {
          patch(item.id, { status: "failed", error: msg });
          // Emit an error class only (never the message text or filename).
          const errorClass =
            /signed url|create-upload-url|Missing signed URL/i.test(msg)
              ? "signed_url_error"
              : /Network error/i.test(msg)
                ? "network_error"
                : /Upload failed \(\d+\)/.test(msg)
                  ? "http_error"
                  : "upload_error";
          onUploadFailed?.({ errorClass });
        }
      } finally {
        controllersRef.current.delete(item.id);
      }
    },
    [source, patch, onUploadComplete, onUploadFailed],
  );

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (disabled) return;
      const current = filesRef.current;
      const currentTotal = current.reduce((s, f) => s + f.size, 0);
      const errors: string[] = [];
      const accepted: UploadedFile[] = [];
      let runningTotal = currentTotal;
      const effectiveMax = singleFile ? 1 : maxFiles;

      const list = singleFile ? incoming.slice(0, 1) : incoming;

      for (const file of list) {
        if (!singleFile && current.length + accepted.length >= effectiveMax) {
          errors.push(`${file.name}: limit is ${effectiveMax} files`);
          continue;
        }
        const ext = extOf(file.name);
        if (!ALLOWED_EXT.has(ext)) {
          errors.push(`${file.name}: unsupported file type`);
          continue;
        }
        if (file.size <= 0) {
          errors.push(`${file.name}: empty file`);
          continue;
        }
        if (file.size > effectiveMaxFileBytes) {
          errors.push(`${file.name}: ${OVERSIZE_HELP}`);
          continue;
        }
        if (runningTotal + file.size > maxTotalBytes) {
          errors.push(
            `${file.name}: would exceed ${formatBytes(maxTotalBytes)} total upload size`,
          );
          continue;
        }
        runningTotal += file.size;
        accepted.push({
          id: randomId(),
          file,
          name: file.name,
          size: file.size,
          contentType: file.type || "application/octet-stream",
          status: "queued",
          progress: 0,
        });
      }

      if (errors.length) {
        toast.error("Some files were skipped", {
          description: errors.slice(0, 3).join("\n\n"),
          duration: 10000,
        });
      }
      if (accepted.length === 0) return;

      const next = singleFile ? accepted : [...current, ...accepted];
      filesRef.current = next;
      onChange(next);
      onUploadStart?.({ count: accepted.length });
      for (const it of accepted) void startUpload(it);
    },
    [disabled, maxFiles, effectiveMaxFileBytes, maxTotalBytes, singleFile, onChange, startUpload, onUploadStart],
  );

  const remove = useCallback(
    (id: string) => {
      const controller = controllersRef.current.get(id);
      controller?.abort();
      controllersRef.current.delete(id);
      const next = filesRef.current.filter((f) => f.id !== id);
      filesRef.current = next;
      onChange(next);
      onUploadRemoved?.({ remaining: next.length });
    },
    [onChange, onUploadRemoved],
  );

  const retry = useCallback(
    (id: string) => {
      const item = filesRef.current.find((f) => f.id === id);
      if (item) void startUpload(item);
    },
    [startUpload],
  );

  useEffect(() => {
    return () => {
      controllersRef.current.forEach((c) => c.abort());
      controllersRef.current.clear();
    };
  }, []);

  const isDark = variant === "dark";
  const borderBase = isDark ? "border-white/15" : "border-black/15";
  const borderHover = isDark ? "hover:border-white/40" : "hover:border-black/40";
  const textPrimary = isDark ? "text-white" : "text-charcoal";
  const textMuted = isDark ? "text-white/55" : "text-charcoal/60";
  const textFaint = isDark ? "text-white/40" : "text-charcoal/50";
  const barTrack = isDark ? "bg-white/10" : "bg-black/10";
  const barFill = isDark ? "bg-white" : "bg-charcoal";
  const rowBorder = isDark ? "border-white/10" : "border-black/10";

  const canAddMore = !singleFile && files.length < maxFiles;

  const aggregate = useMemo(() => activeUploadProgress(files), [files]);
  const activeFiles = files.filter((f) => f.status === "uploading" || f.status === "queued");
  const allDone = files.length > 0 && files.every((f) => f.status === "uploaded");
  useUnloadWarning(activeFiles.length > 0);

  return (
    <div className="space-y-4">
      {(label || files.length > 0) && (
        <div className="flex items-center justify-between gap-3">
          {label ? (
            <p className={`font-sans font-light text-sm ${textMuted}`}>{label}</p>
          ) : (
            <span />
          )}
          {files.length > 0 && (
            <LogoBars
              variant={aggregate == null ? "idle" : "progress"}
              progress={aggregate == null ? 4 : aggregate / 25}
              tone={isDark ? "light" : "dark"}
              size="xs"
              ariaLabel="codelumen"
            />
          )}
        </div>
      )}

      {(canAddMore || singleFile) && (
        <label
          htmlFor={`fu-${source}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const dropped = Array.from(e.dataTransfer.files ?? []);
            if (dropped.length) addFiles(dropped);
          }}
          className={`flex flex-col items-center justify-center text-center gap-3 cursor-pointer border border-dashed rounded-sm px-6 py-8 sm:py-10 transition-colors duration-300 ${
            dragOver
              ? isDark ? "border-white/60 bg-white/[0.03]" : "border-charcoal/60 bg-black/[0.03]"
              : `${borderBase} ${borderHover}`
          } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
        >
          <Upload className={`h-5 w-5 ${textMuted}`} strokeWidth={1.2} aria-hidden />
          <div className="space-y-1">
            <p className={`font-sans font-light text-sm ${textPrimary}`}>
              {singleFile && files.length > 0
                ? "Tap to replace file"
                : files.length > 0
                  ? "Tap to add another file"
                  : (helperText ?? "Tap to choose files or drag & drop")}
            </p>
            <p className={`font-sans font-light text-[11px] leading-relaxed ${textFaint}`}>
              PDF, DWG, DXF, XLS, DOC, JPG, PNG, ZIP · up to {formatBytes(effectiveMaxFileBytes)} each · {formatBytes(maxTotalBytes)} total
            </p>
          </div>
          <span
            className={`mt-1 inline-flex items-center justify-center min-h-[40px] px-5 border font-sans font-light text-[11px] tracking-[0.2em] uppercase transition-colors ${
              isDark
                ? "border-white/30 text-white hover:border-white/70"
                : "border-charcoal/30 text-charcoal hover:border-charcoal/70"
            }`}
          >
            Choose Files
          </span>
          <input
            id={`fu-${source}`}
            ref={inputRef}
            type="file"
            multiple={!singleFile}
            accept={DEFAULT_ACCEPT}
            className="sr-only"
            disabled={disabled}
            onChange={(e) => {
              const list = Array.from(e.target.files ?? []);
              if (list.length) addFiles(list);
              if (inputRef.current) inputRef.current.value = "";
            }}
          />
        </label>
      )}

      {(activeFiles.length > 0 || allDone) && (
        <div className={`border ${rowBorder} px-4 py-3`} aria-live="polite">
          <UploadProgressMark
            percent={aggregate}
            activeCount={activeFiles.length}
            totalCount={files.length}
            tone={isDark ? "dark" : "light"}
            status={
              allDone
                ? `${files.length} file${files.length === 1 ? "" : "s"} uploaded · ready to submit`
                : undefined
            }
          />
        </div>
      )}

      {files.length > 0 && (
        <ul className="space-y-3">
          {files.map((f) => {
            const statusLabel =
              f.status === "queued"
                ? "Queued"
                : f.status === "uploading"
                  ? `Uploading · ${f.progress}%`
                  : f.status === "uploaded"
                    ? "Uploaded"
                    : `Failed · ${f.error ?? "error"}`;
            return (
              <li
                key={f.id}
                className={`border ${rowBorder} px-4 py-3`}
                aria-live="polite"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {f.status === "uploaded" && (
                        <Check
                          className={`h-4 w-4 shrink-0 ${textPrimary}`}
                          strokeWidth={1.5}
                          aria-hidden
                        />
                      )}
                      <p className={`font-sans font-light text-sm truncate ${textPrimary}`}>
                        {f.name}
                      </p>
                    </div>
                    <p className={`mt-1 font-sans font-light text-[11px] ${textMuted}`}>
                      {formatBytes(f.size)} · {statusLabel}
                    </p>
                    {(f.status === "uploading" || f.status === "queued") && (
                      <div
                        className={`mt-2 h-[2px] w-full overflow-hidden ${barTrack}`}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={f.progress}
                      >
                        <div
                          className={`h-full ${barFill} transition-[width] duration-150`}
                          style={{ width: `${f.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {f.status === "failed" && (
                      <button
                        type="button"
                        onClick={() => retry(f.id)}
                        aria-label={`Retry upload of ${f.name}`}
                        className={`p-1 ${textMuted} hover:${textPrimary} transition-colors`}
                      >
                        <RotateCw className="h-4 w-4" strokeWidth={1.2} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(f.id)}
                      aria-label={`Remove ${f.name}`}
                      className={`p-1 ${textMuted} hover:${textPrimary} transition-colors`}
                    >
                      <X className="h-4 w-4" strokeWidth={1.2} />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FileUploader;

export function uploadsAreReady(files: UploadedFile[]): boolean {
  if (files.length === 0) return true;
  return files.every((f) => f.status === "uploaded" && !!f.path);
}

export function activeUploadProgress(files: UploadedFile[]): number | null {
  const active = files.filter(
    (f) => f.status === "uploading" || f.status === "queued",
  );
  if (active.length === 0) return null;
  const total = active.reduce((s, f) => s + f.size, 0);
  if (total === 0) return 0;
  const done = active.reduce(
    (s, f) => s + (f.size * (f.progress / 100)),
    0,
  );
  return Math.round((done / total) * 100);
}

export function uploadRefs(files: UploadedFile[]) {
  return files
    .filter((f) => f.status === "uploaded" && f.path)
    .map((f) => ({
      path: f.path!,
      filename: f.name,
      size: f.size,
      content_type: f.contentType,
    }));
}
